//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import ItemTypesModel from './itemTypes.model';

/**
 * @service ItemTypesService
 * @description Service for managing Item Type definitions within the Material Management System (MMS).
 * Item Types serve as the fundamental classification layer for the inventory system. They categorize
 * items into distinct groups such as 'Feed', 'Medicine', 'Chemicals', 'Machinery', etc.
 *
 * This classification is crucial for:
 * 1. **Inventory Organization**: Grouping similar items for reporting and tracking.
 * 2. **Business Logic**: Different item types may trigger different workflows (e.g., Feed items have nutritional analysis, Medicines have expiry tracking).
 * 3. **UI Filtering**: Allowing users to filter dropdowns and lists based on the type of item they are looking for.
 * 4. **Validation**: Enforcing specific data requirements based on the item type selected during item creation.
 *
 * This service provides the interface to retrieve these definitions from the backend API.
 * It extends the generic `Service` class to inherit standard CRUD capabilities and HTTP handling.
 *
 * @feature 1. Retrieve all defined Item Types from the system.
 * @feature 2. Support for pagination and sorting (defaulted to large page size for lookup lists).
 * @feature 3. Management of concurrent HTTP requests to prevent memory leaks.
 *
 * @architecture
 * Extends base Service<ItemTypesModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /mms/itemtypes/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class ItemTypesService extends Service<ItemTypesModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/mms/itemtypes/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/mms/itemtypes/');
  }

  /**
   * @method getAllItemTypes
   * @async
   * @description Fetch all item type records from the backend.
   *
   * This method performs a GET request to the service endpoint. It is configured to retrieve
   * a large number of records (pageSize=2000) to ensure all types are loaded for dropdowns/lookups,
   * ordered by creation date ('new').
   *
   * @returns {Promise<Array<ItemTypesModel>>} Promise resolving to an array of ItemTypesModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * itemTypesService.getAllItemTypes().then(types => {
   *   console.log('Available Item Types:', types);
   * }).catch(error => {
   *   console.error('Failed to load item types', error);
   * });
   */
  public getAllItemTypes() {
    return new Promise<Array<ItemTypesModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ItemTypesModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background
   * or attempt to update destroyed views.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
